import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { EMPTY_METRIC_FILTERS } from "../../lib/dashboardData";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import {
  Badge,
  DataTable,
  Inline,
  InlineError,
  RouterLink,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { DateCell } from "../shared/DateCell";
import {
  batchDisplayLabel,
  historyStatusTone,
} from "../simulations/batchPresentation";

type BatchResponse = components["schemas"]["BatchResponse"];
type MetricsQueryResponse = components["schemas"]["MetricsQueryResponse"];

// ASSUMED(the unspecified "last N" template lineage window is five runs.)
export const TEMPLATE_BATCH_LINEAGE_LIMIT = 5;

export interface PassFailRollup {
  fail: number;
  pass: number;
  warn: number;
}

type RollupState =
  | { kind: "error"; message: string }
  | { kind: "loading" }
  | { kind: "ready"; rollup: PassFailRollup };

export function passFailRollup(response: MetricsQueryResponse): PassFailRollup {
  const rollup: PassFailRollup = { fail: 0, pass: 0, warn: 0 };
  for (const series of response.series ?? []) {
    if (
      series.key !== "pass" &&
      series.key !== "warn" &&
      series.key !== "fail"
    ) {
      continue;
    }
    const key = series.key;
    for (const group of series.groups ?? []) {
      for (const point of group.points ?? []) {
        if (point.kind === "scalar") {
          rollup[key] += point.value;
        }
      }
    }
  }
  return rollup;
}

function rollupLabel(state: RollupState) {
  if (state.kind === "loading") {
    return <Text variant="muted">Loading rollup…</Text>;
  }
  if (state.kind === "error") {
    return (
      <Text title={state.message} variant="muted">
        Rollup unavailable
      </Text>
    );
  }
  const total = state.rollup.pass + state.rollup.warn + state.rollup.fail;
  if (total === 0) {
    return <Text variant="muted">No graded results yet</Text>;
  }
  return (
    <Inline gap={2}>
      <Badge tone="success">Pass {state.rollup.pass}</Badge>
      {state.rollup.warn > 0 ? (
        <Badge tone="warning">Warn {state.rollup.warn}</Badge>
      ) : null}
      <Badge tone="danger">Fail {state.rollup.fail}</Badge>
    </Inline>
  );
}

function lineageColumns(
  agentId: string,
  rollups: ReadonlyMap<string, RollupState>,
): Array<ColumnDef<BatchResponse>> {
  return [
    {
      header: "Batch",
      id: "batch",
      accessorFn: batchDisplayLabel,
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge tone={historyStatusTone(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Pass / fail",
      id: "rollup",
      cell: ({ row }) =>
        rollupLabel(rollups.get(row.original.id) ?? { kind: "loading" }),
    },
    {
      header: "Launched",
      accessorKey: "created_at",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
    {
      header: "Results",
      id: "results",
      cell: ({ row }) => (
        <RouterLink
          search={{ agent: agentId, batch: row.original.id }}
          to="/transcripts"
        >
          View in Transcripts
        </RouterLink>
      ),
    },
  ];
}

export interface TemplateBatchLineageProps {
  agentId: string;
  templateId: string;
}

export function TemplateBatchLineage({
  agentId,
  templateId,
}: TemplateBatchLineageProps) {
  const batches = useListQuery("/v1/batches", {
    pageSize: TEMPLATE_BATCH_LINEAGE_LIMIT,
    params: { template_id: templateId },
    queryKey: queryKeys.batches.list({
      surface: "template-lineage",
      template_id: templateId,
    }),
  });
  const rollupQueries = useQueries({
    queries: batches.items.map((batch) => ({
      refetchInterval:
        batch.status === "pending" || batch.status === "running"
          ? 10_000
          : false,
      queryKey: queryKeys.metrics.list({
        batch_id: batch.id,
        surface: "template-lineage",
      }),
      queryFn: async () => {
        const { data, error } = await apiClient.POST("/v1/metrics:query", {
          body: {
            aggregate: "default",
            bucket: "none",
            filters: { ...EMPTY_METRIC_FILTERS, batch_id: batch.id },
            group_by: [],
            metric: "marker_pass_rate",
            time_range: null,
          },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      },
    })),
  });
  const rollups = useMemo(() => {
    const states = new Map<string, RollupState>();
    batches.items.forEach((batch, index) => {
      const query = rollupQueries[index];
      if (query?.data !== undefined) {
        states.set(batch.id, {
          kind: "ready",
          rollup: passFailRollup(query.data),
        });
      } else if (query?.isError === true) {
        states.set(batch.id, {
          kind: "error",
          message: requestErrorDescription(query.error),
        });
      } else {
        states.set(batch.id, { kind: "loading" });
      }
    });
    return states;
  }, [batches.items, rollupQueries]);

  if (batches.isPending) {
    return (
      <Skeleton label="Loading template runs" rows={3} shape="table-row" />
    );
  }
  if (batches.errorMessage !== undefined && batches.items.length === 0) {
    return <InlineError>{batches.errorMessage}</InlineError>;
  }
  if (batches.items.length === 0) {
    return (
      <Text variant="muted">This template has not produced a batch yet.</Text>
    );
  }
  return (
    <Stack gap={2}>
      <Text variant="caps">Recent runs</Text>
      <DataTable
        ariaLabel="Template batch lineage"
        columns={lineageColumns(agentId, rollups)}
        data={batches.items}
        getRowId={(batch) => batch.id}
      />
      {batches.hasNextPage ? (
        <Text variant="muted">
          Showing the {TEMPLATE_BATCH_LINEAGE_LIMIT} most recent runs.
        </Text>
      ) : null}
      {batches.errorMessage !== undefined ? (
        <InlineError>{batches.errorMessage}</InlineError>
      ) : null}
    </Stack>
  );
}
